import { motion } from "framer-motion";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Link, useLocation } from "wouter";
import { companies, activities } from '../../../db';
import { createCompanySlug } from "@/lib/companyUtils";
import { getCompanyDisplayInfo } from "@/lib/companyDisplayUtils";
import DualCompanyLogo from "@/components/ui/dual-company-logo";

// Function to get photo height based on image aspect ratio for fixed width of 320px (w-80)
const getPhotoHeight = (companyId: number) => {
  const fixedWidth = 320; // 320px = w-80
  switch (companyId) {
    case 1: // Mercedes-Benz (2048x1152, ratio 1.78:1)
      return Math.round(fixedWidth / 1.78); // ~180px
    case 2: // FERCHAU (2946x1756, ratio 1.678:1)
      return Math.round(fixedWidth / 1.678); // ~191px
    case 3: // Siemens (1188x690, ratio 1.72:1)
      return Math.round(fixedWidth / 1.72); // ~186px
    case 4: // THB (1090x616, ratio 1.77:1)
      return Math.round(fixedWidth / 1.77); // ~181px
    case 5: // University of Rostock (1402x540, ratio 2.596:1)
      return Math.round(fixedWidth / 2.596); // ~123px
    default:
      return 180; // fallback
  }
};

export default function CompanyTimeline() {
  const [, setLocation] = useLocation();
  const currentActivity = activities
    .filter(activity => !activity.endDate)[0];
    
  const uniqueCompanies = activities
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
    .reduce((acc, activity) => {
      // Add assignment company if not already in the list
      if (!acc.find(c => c.id === activity.companyId)) {
        const company = companies.find(c => c.id === activity.companyId);
        if (company) {
          acc.push(company);
        }
      }
      
      // Add temporary employment agency if not already in the list
      if (activity.tempAgencyId && !acc.find(c => c.id === activity.tempAgencyId)) {
        const tempAgency = companies.find(c => c.id === activity.tempAgencyId);
        if (tempAgency) {
          acc.push(tempAgency);
        }
      }
      
      return acc;
    }, [] as typeof companies);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex justify-center gap-4 mt-4 mb-8"
    >
      {uniqueCompanies.map((company) => {
        // Check if this company is involved in current activity (either as assignment or temp agency)
        const currentActivityWithThisCompany = currentActivity && (
          currentActivity.companyId === company.id || 
          currentActivity.tempAgencyId === company.id
        ) ? currentActivity : null;
        
        const displayInfo = currentActivityWithThisCompany 
          ? getCompanyDisplayInfo(currentActivityWithThisCompany, companies)
          : null;
        
        // Show dual logo only if this is the assignment company in a dual company relationship
        const shouldShowDualLogo = displayInfo?.isDualCompany && 
          displayInfo.assignmentCompany.id === company.id;
        
        return (
          <div key={company.id}>
            {shouldShowDualLogo ? (
              <DualCompanyLogo
                assignmentCompany={displayInfo.assignmentCompany}
                tempAgency={displayInfo.tempAgency!}
                size="sm"
                className={currentActivityWithThisCompany ? 'logo-glow' : ''}
                onAssignmentCompanyClick={() => {
                  setLocation(`/professional/${createCompanySlug(displayInfo.assignmentCompany.name)}`);
                }}
                onTempAgencyClick={() => {
                  setLocation(`/professional/${createCompanySlug(displayInfo.tempAgency!.name)}`);
                }}
              />
            ) : (
              <div className="relative group">
                <Link href={`/professional/${createCompanySlug(company.name)}`}>
                  <a>
                    <Avatar 
                      className={`w-8 h-8 bg-transparent hover:scale-110 transition-transform cursor-pointer ${currentActivityWithThisCompany ? 'logo-glow' : ''}`}
                    >
                      <AvatarImage
                        src={company.logoPath || undefined}
                        alt={`${company.name} logo`}
                        className="object-cover p-0"
                      />
                      <AvatarFallback>{company.logoText}</AvatarFallback>
                    </Avatar>
                  </a>
                </Link>
                
                {/* Hover popup with company photo */}
                {company.photoPath && company.website && (
                  <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none group-hover:pointer-events-auto z-50">
                    {/* Invisible bridge area to keep hover active */}
                    <div className="absolute top-full w-full h-4 bg-transparent"></div>
                    <div className="bg-white rounded-lg shadow-lg border overflow-hidden w-80">
                      <a 
                        href={company.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block hover:opacity-80 transition-opacity"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <img 
                          src={company.photoPath} 
                          alt={`${company.name} photo`}
                          className="w-full cursor-pointer object-cover"
                          style={{ height: `${getPhotoHeight(company.id)}px` }}
                        />
                      </a>
                      {/* Map section */}
                      {company.mapsLink && (
                        <div className="border-t">
                          <iframe
                            src={company.mapsLink}
                            width="320"
                            height="140"
                            style={{ border: 0 }}
                            allowFullScreen={false}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            className="w-full"
                          />
                        </div>
                      )}
                    </div>
                    {/* Arrow pointing down to the logo */}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-white"></div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </motion.div>
  );
}
